import type {
  DiagnosticsReport,
  StorageInfo,
  ConnectionMonitor,
} from './types';

export function getConnectionInfo(): ConnectionMonitor {
  const nav = navigator as any;
  const connection =
    nav.connection || nav.mozConnection || nav.webkitConnection;

  return {
    isOnline: navigator.onLine,
    connectionType: connection?.type || null,
    effectiveType: connection?.effectiveType || null,
    downlink: connection?.downlink || null,
    rtt: connection?.rtt || null,
    saveData: connection?.saveData || false,
  };
}

export function collectEnvironmentBasics() {
  const ua = navigator.userAgent || '';
  const screenInfo = {
    width: window.screen.width,
    height: window.screen.height,
  };
  const timezone = (() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) {
      return undefined;
    }
  })();

  return {
    userAgent: ua,
    language: navigator.language,
    languages: navigator.languages as string[] | undefined,
    hardwareConcurrency: (navigator as any).hardwareConcurrency,
    deviceMemory: (navigator as any).deviceMemory,
    online: navigator.onLine,
    dpr: window.devicePixelRatio,
    screen: screenInfo,
    timezone,
    connection: getConnectionInfo(),
  } as DiagnosticsReport['environment'];
}

export async function collectStorageInfo(): Promise<StorageInfo> {
  // localStorage test
  let localStorageWritable = false;
  try {
    const key = `diag_${Date.now()}`;
    localStorage.setItem(key, '1');
    localStorage.removeItem(key);
    localStorageWritable = true;
  } catch (e) {
    localStorageWritable = false;
  }

  // cookies test
  let cookiesWritable = false;
  try {
    const cookieKey = `diag_${Date.now()}`;
    document.cookie = `${cookieKey}=1;path=/`; // session cookie
    cookiesWritable = document.cookie.indexOf(cookieKey) !== -1;
  } catch (e) {
    cookiesWritable = false;
  }

  const estimate = (navigator as any).storage?.estimate
    ? await (navigator as any).storage.estimate()
    : undefined;

  return {
    localStorageWritable,
    cookiesWritable,
    storageEstimate: estimate
      ? { quota: estimate.quota, usage: estimate.usage }
      : undefined,
  };
}

export function collectAppContext(signalRState?: string) {
  return {
    location: {
      href: window.location.href,
      pathname: window.location.pathname,
    },
    env: {
      API_URL: process.env.API_URL,
      CDN_URL: process.env.CDN_URL,
      UPLOADS_CDN_URL: process.env.UPLOADS_CDN_URL,
      SIGNALR_HUB_NAME: process.env.SIGNALR_HUB_NAME,
    },
    route: window.location.pathname,
    signalRState,
  } as DiagnosticsReport['app'];
}
